import React from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, BadgeCheck, Bookmark, Eye, Send, Share2 } from 'lucide-react'
import { MarketplaceProfile, ShopSetupChecklist, type ShopStepId } from '@adthlete/shared-frontend'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/sonner'
import { useAuth } from '@/hooks/useAuth'
import { useCollaborations } from '@/hooks/useCollaborations'
import { useGetMyMarketplaceProfileQuery } from '@/redux/collaborationsApi'
import { athleteProgress, countCompletedDeals } from '@/lib/athleteScore'

/** A note inside the brand's view, pointing at what to fix (light, like the page it sits on) */
const Note: React.FC<{ children: React.ReactNode; to: string; cta: string }> = ({
  children,
  to,
  cta,
}) => {
  const navigate = useNavigate()
  return (
    <div className="mb-4 flex flex-col gap-2 rounded-xl border border-[#FCD34D] bg-[#FFFBEB] p-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-[#78350F]">{children}</p>
      <button
        type="button"
        onClick={() => navigate(to)}
        className="inline-flex shrink-0 items-center justify-center gap-1 rounded-lg bg-[#FCD34D] px-3 py-2 text-sm font-semibold text-[#78350F] hover:bg-[#FBBF24]"
      >
        {cta}
        <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  )
}

// Where each set-up step is done in the athlete app
const STEP_PATH: Record<ShopStepId, string> = {
  profile: '/setup/about',
  socials: '/setup/connect',
  series: '/setup/themes',
  prices: '/setup/rates',
  open: '/setup/publish',
}
const STEP_BUTTON: Record<ShopStepId, string> = {
  profile: 'Add',
  socials: 'Connect',
  series: 'Build',
  prices: 'Set prices',
  open: 'Open',
}

/**
 * "How brands see you": the athlete's own page exactly as brands see it in the Marketplace, with
 * notes on anything that's missing, like unverified numbers or no rate card. Fixing each one
 * makes the page stronger. The Athios Lab can build it all from the manager app.
 */
const BrandView: React.FC = () => {
  const navigate = useNavigate()
  const { athlete } = useAuth()
  const { collaborations } = useCollaborations()
  const progress = athleteProgress(athlete ?? {}, {
    completedDeals: countCompletedDeals(collaborations),
  })
  const { data, isLoading, isError } = useGetMyMarketplaceProfileQuery()
  const quest = (id: string) => progress.quests.find((q) => q.id === id)

  if (isLoading) return <p className="text-sm text-gray-500">Loading your page…</p>
  if (isError || !data) {
    return <p className="text-sm text-gray-500">We couldn't load your page. Try again soon.</p>
  }
  const first = data.athlete.name.split(' ')[0]
  const shareShop = async () => {
    if (!data.shopUrl) return
    try {
      await navigator.clipboard.writeText(data.shopUrl)
      toast('Link to your shop copied', {
        description:
          data.availability.openToOffers && data.availability.published
            ? 'Send it to brands. They can look without an account and book in a tap.'
            : 'Open your shop first so brands can see it.',
      })
    } catch {
      toast('Copy your shop link', { description: data.shopUrl })
    }
  }
  const verified = data.channels.some((c) => c.verified)
  const socials = quest('socials')
  const rates = quest('rate_card')
  const profile = quest('profile')

  return (
    <div className="mx-auto max-w-[1180px] space-y-5 pb-10">
      <div>
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#4d7c0f]">
          <Eye className="h-4 w-4" /> How brands see you
        </p>
        <h1 className="text-2xl font-bold mt-1 text-foreground">Your Content Shop</h1>
        <p className="text-sm text-gray-600">
          This is exactly what a brand sees when they find you: your content, ready to book. The
          stronger it looks, the more offers you get and the more you can charge.
        </p>
      </div>

      {/* The five steps that make the shop sell: the same list the manager sees */}
      <ShopSetupChecklist
        data={data}
        actionFor={(step) => ({
          label: STEP_BUTTON[step.id],
          onClick: () => navigate(STEP_PATH[step.id]),
        })}
        doneAction={
          <Button variant="lime" onClick={shareShop}>
            <Share2 className="h-4 w-4" /> Share my shop
          </Button>
        }
      />

      {/* The brand's view, on the light background brands see it on */}
      <div className="rounded-3xl bg-[#F5F6F2] p-3 sm:p-5">
        <MarketplaceProfile
          data={data}
          notes={{
            hero:
              profile && !profile.done ? (
                <Note to={profile.to} cta="Finish your shop">
                  Brands skip shops without a photo, bio, sport and school.
                </Note>
              ) : undefined,
            channels: !verified ? (
              <Note
                to={socials?.to ?? '/setup/connect'}
                cta="Verify my numbers"
              >
                <BadgeCheck className="mr-1 inline h-4 w-4 text-[#2563EB]" />
                Connect your accounts so brands see verified numbers. Brands trust them, and
                they book verified athletes first.
              </Note>
            ) : undefined,
            audience: !data.audience ? (
              <Note to={socials?.to ?? '/setup/connect'} cta="Connect an account">
                Brands look at who your followers are. Connect an account to show your audience.
              </Note>
            ) : undefined,
            rates: !data.rateCard ? (
              <Note
                to={rates?.to ?? '/setup/rates'}
                cta="Set your rate card"
              >
                With a rate card, brands can book you at your prices in one step.
              </Note>
            ) : (
              <div className="mb-3 flex justify-end">
                <button
                  type="button"
                  onClick={() => navigate('/setup/rates')}
                  className="text-sm font-semibold text-[#3F7A0A] hover:underline"
                >
                  Edit my rates and licensing
                </button>
              </div>
            ),
            themes: !data.themes.length ? (
              <Note to="/setup/themes" cta="Pick your themes">
                Pick the formats you post again and again, so brands can book them.
              </Note>
            ) : data.themes.some((t) => !t.example) ? (
              <Note to="/setup/themes" cta="Add examples">
                Add an example post to each theme. Brands want to see you really make it.
              </Note>
            ) : undefined,
          }}
          actions={
            <>
              <p className="text-center text-xs text-[#6B7280]">What brands can do in your shop</p>
              <Button disabled className="h-11 w-full bg-[#C0FD72] text-[#111827] opacity-100">
                <Send className="h-4 w-4" /> Book {first}
              </Button>
              <Button
                disabled
                variant="outline"
                className="h-11 w-full border-[#E5E7EB] bg-[#FFFFFF] text-[#111827] opacity-100"
              >
                <Bookmark className="h-4 w-4" /> Save to shortlist
              </Button>
              {/* The one button that works here: send brands the link to their shop */}
              <Button
                variant="outline"
                disabled={!data.shopUrl}
                onClick={shareShop}
                className="h-11 w-full border-[#C0FD72] bg-[#FFFFFF] text-[#111827] hover:bg-[#F7FEE7]"
              >
                <Share2 className="h-4 w-4" /> Share my shop
              </Button>
            </>
          }
        />
      </div>
    </div>
  )
}

export default BrandView
